import { Component, OnInit } from '@angular/core';
import { AlertService } from 'src/app/shared/services/alert.service';
import { UntypedFormGroup } from '@angular/forms';
import { Actions, ofType } from '@ngrx/effects';
import { State } from '../../../../core/store';
import { Store } from '@ngrx/store';
import { BaseComponent } from '../../../../core/abstracts/baseComponent';
import { TimeOffClosedPeriodWarningModel, TimeOffModel } from '../../../../models/timeoffModel';
import { initEditTimeOffRequestForm } from '../../../../core/store/timeoff/timeoff.reducer';
import { getAllControlsErrors, getFormattedTime, markFormGroupTouched, updateFormGroupValidity } from '../../../../helpers/utils';
import { closeEditTimeOffRequest, getEmployeePtoSummary, saveTimeOffRequest, saveTimeOffRequestSuccess, updateTimeOffRequestForm } from '../../../../core/store/timeoff/timeoff.actions';
import { tap } from 'rxjs';
import { SelectItem } from 'primeng/api';
import { loadEmployeeUsers } from '../../../../core/store/employee/employee.action';
import { UserAutoCompleteItem } from '../../../../models/utilModels';
import { TimeoffService } from '../../../../core/services/timeoff.service';
import * as moment from 'moment';

@Component({
    selector: 'app-edit-timeoff-request',
    templateUrl: './edit-timeoff-request.component.html',
    styleUrls: ['./edit-timeoff-request.component.scss'],
    standalone: false
})
export class EditTimeoffRequestComponent extends BaseComponent implements OnInit {
  display: boolean = false;
  title: string;
  form: UntypedFormGroup;
  disableButtons: boolean = true;
  timeOffRequest: TimeOffModel = {} as TimeOffModel;
  userId: number;
  employeeName: string;
  totalAvailable: string;
  disableName: boolean = true;
  currentUserId: number;
  checkingClosedPeriodWarning: boolean = false;

  prefixText: string;
  selectedUserEmployee: UserAutoCompleteItem;
  userList: UserAutoCompleteItem[];

  constructor(
    private actions$: Actions,
    private store: Store<State>,
    private alertService: AlertService,
    private timeoffService: TimeoffService) {
    super();
  }

  ngOnInit() {
    this.form = initEditTimeOffRequestForm({} as TimeOffModel);
    this.subs.push(
      this.store.select(s => s.accountState.user).subscribe(user => {
        this.employeeName = user.firstName + " " + user.lastName;
        this.currentUserId = user.id;
      }),
      this.store.select(s => s.timeoffState.employeePtoSummary).subscribe(summary => {
        if (summary == null) {
          this.totalAvailable = "";
          return;
        }
        
        this.totalAvailable = summary.totalAvailableString;
      }),
      this.store.select(s => s.timeoffState.editingTimeOffRequest).subscribe(editingTimeOffRequest => {
        this.display = editingTimeOffRequest;
      }),
      this.store.select(s => s.timeoffState.savingTimeOffRequest).subscribe(savingTimeOffRequest => {
        this.disableButtons = savingTimeOffRequest;
      }),
      this.store.select(s => s.timeoffState.disableEmployeeName).subscribe(disableName => {
        this.disableName = disableName;
      }),
      this.store.select(s => s.timeoffState.selectedTimeOffRequest).subscribe(request => {
        if (request === null) {
          this.timeOffRequest = null;
          return;
        }
        
        const changedRequest = !this.timeOffRequest || (this.timeOffRequest.timeOffId !== request.timeOffId);
        this.timeOffRequest = JSON.parse(JSON.stringify(request));

        if (changedRequest) {
          if (this.timeOffRequest != null) {
            this.timeOffRequest.ptoHours = this.timeOffRequest.ptoMinutesUsed >= 0 ?
              Math.floor(this.timeOffRequest.ptoMinutesUsed / 60) :
              Math.ceil(this.timeOffRequest.ptoMinutesUsed / 60);

            this.timeOffRequest.ptoMinutes = this.timeOffRequest.ptoMinutesUsed % 60;
          }

          this.form = initEditTimeOffRequestForm(this.timeOffRequest);          
          this.subs.push(
            this.form.valueChanges.subscribe(formValues => {
              const formErrors = getAllControlsErrors(this.form);
              this.store.dispatch(updateTimeOffRequestForm({ formValues, formErrors }));
            })
          )
          if (typeof request.timeOffId === 'undefined' || request.timeOffId === null || request.timeOffId < 1) {
            this.title = "New Time Off Request";
            if (request.employeeName != null && request.userId != null && request.userId > 0) {
              this.prefixText = request.employeeName;
              this.userId = request.userId
            }
            else {
              this.prefixText = this.employeeName;
              this.userId = this.currentUserId;
            }
            this.store.dispatch(updateTimeOffRequestForm({ formValues: this.form.value, formErrors: getAllControlsErrors(this.form) }));
          } else {
            this.title = "Edit Time Off Request #" + request.timeOffId;
            this.timeOffRequest.startDateString = getFormattedTime(request.startDate);
            this.timeOffRequest.endDateString = getFormattedTime(request.endDate);
            this.timeOffRequest.startTimeString = getFormattedTime(request.startTime);
            this.timeOffRequest.endTimeString = getFormattedTime(request.endTime);            
            this.prefixText = request.employeeName;
            this.userId = request.userId;
          }

          this.loadPtoSummaryInfo();
        }
      }),
      this.store.select(s => s.employeeState.employeeUsers).subscribe(users => {
        this.userList = users.map(x => ({
          id: x.value,
          name: x.label
        }));
      }),
      this.actions$.pipe(
        ofType(saveTimeOffRequestSuccess),
        tap(() => {
          this.closeModal();
        })
      ).subscribe(),
    );
  }


  closeModal() {
    this.store.dispatch(closeEditTimeOffRequest());
  }

  saveTimeOffRequest() {
    if (this.checkingClosedPeriodWarning) {
      return;
    }

    updateFormGroupValidity(this.form);
    if (this.form.valid) {
      this.timeOffRequest.userId = this.userId;
      this.checkingClosedPeriodWarning = true;
      this.timeoffService.getTimeOffClosedPeriodWarning(this.timeOffRequest).subscribe(
        warning => {
          this.checkingClosedPeriodWarning = false;
          this.confirmClosedPeriodWarning(warning);
        },
        error => {
          this.checkingClosedPeriodWarning = false;
          this.alertService.error(error?.error || "Error checking closed time off periods.");
        }
      );
    }
    else {
      markFormGroupTouched(this.form);
    }
  }

  confirmClosedPeriodWarning(warning: TimeOffClosedPeriodWarningModel) {
    if (warning && warning.hasWarning) {
      const message = warning.message || "Time off requests are closed for this time period. You can still submit, but this may not be approved.";
      this.alertService.confirm("Time Off Requests Closed", message).subscribe(answer => {
        if (answer) {
          this.confirmApprovedEditWarning();
        }
      });

      return;
    }

    this.confirmApprovedEditWarning();
  }

  confirmApprovedEditWarning() {
    const requestStart = this.timeOffRequest.allDay
      ? moment(this.timeOffRequest.startDateString, 'YYYY-MM-DD', true).startOf('day')
      : moment(`${this.timeOffRequest.startDateString} ${this.timeOffRequest.startTimeString}`, 'YYYY-MM-DD h:mm A', true);

    if (this.timeOffRequest.approved == true && requestStart.isValid() && requestStart.isAfter(moment())) {
      this.alertService.confirm("Confirm Edit", "Editing this request will revert the status to Pending. Are you sure you want to continue?")
        .subscribe(
          anwser => {
            if (anwser) {
              this.timeOffRequest.approved = false;
              this.timeOffRequest.denied = false;
              this.dispatchSaveTimeOffRequest();
            }
          }, error => {
          }
        );
    }
    else {
      this.dispatchSaveTimeOffRequest();
    }
  }

  dispatchSaveTimeOffRequest() {
    this.store.dispatch(saveTimeOffRequest({ timeOffRequest: this.timeOffRequest }));
  }

  getStaffList(event) {
    this.store.dispatch(loadEmployeeUsers({
      prefixText: event.query,
      programId: 0,
      payrollClassificationId: 0,
      showInactive: false,
      showTerminated: false
    }));
  }

  selectStaff(selectedUser) {
    if (typeof selectedUser.value.id !== 'number') {
      return;
    }

    this.userId = selectedUser.value.id;
    this.prefixText = selectedUser.value.name
  }

  clearStaff(event) {
   
    this.userId = 0;
    this.prefixText = "";
  }

  loadPtoSummaryInfo() {
    if (this.userId < 1) {
      this.totalAvailable = "";      
      return;
    }

    this.store.dispatch(getEmployeePtoSummary({ userId: this.userId }));
  }
}
